<template>
    <div :class="`star-rating-${align} star-rating`">
        <label for="rating">{{ fieldLabel }}</label>
        <div class="rating-container">
            <div class="stars">
                <span
                    v-for="(star, index) in 5"
                    :key="index"
                    :class="['star', { 'rated': index < rating, 'hovered': index < hoveredRating }]"
                    @click="setRating(index + 1)"
                    @mouseenter="setHoveredRating(index + 1)"
                    @mouseleave="clearHoveredRating">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                </span>
            </div>
            <div class="rating-text" v-if="rating > 0">
                <span class="rating-value">{{ rating }}</span>
                <span class="rating-label">/ 5</span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "FormStarRating",
    props: {
        fieldLabel: {
            type: String,
            required: true,
        },
        align: {
            type: String,
            required: true,
        },
        value: {
            type: [Object, String],
            default: () => ({ rating: 0 })
        },
        formId: {
            type: String,
            required: true,
        },
        order: {
            type: Number,
            required: true,
        }
    },
    data() {
        const safeValue = (typeof this.value === 'object' && this.value !== null) ? this.value : {};
        return {
            rating: safeValue.rating || 0,
            hoveredRating: 0,
        };
    },
    watch: {
        value: {
            handler(newValue) {
                const safeValue = (typeof newValue === 'object' && newValue !== null) ? newValue : {};
                this.rating = safeValue.rating || 0;
            },
            deep: true
        }
    },
    methods: {
        setRating(value) {
            this.rating = value;
            this.emitValues();
        },
        setHoveredRating(value) {
            this.hoveredRating = value;
        },
        clearHoveredRating() {
            this.hoveredRating = 0;
        },
        emitValues() {
            const ratingData = {
                rating: this.rating,
                fieldLabel: this.fieldLabel
            };
            this.$emit("input", ratingData);
            localStorage.setItem(`${this.formId}-${this.order}`, JSON.stringify(ratingData));
        },
        loadValues() {
            const savedData = localStorage.getItem(`${this.formId}-${this.order}`);
            if (savedData) {
                const parsedData = JSON.parse(savedData);
                this.rating = parsedData.rating || 0;
            }
        }
    },
    mounted() {
        this.loadValues();
        this.emitValues();
    }
};
</script>

<style lang="scss" scoped>
.star-rating {
    width: 100%;
    margin: 0 auto;
    font-family: var(--font-family);
}

// Align sistem - left
.star-rating-left {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);
    
    > label {
        width: clamp(140px, 20vw, 200px);
        font-size: clamp(0.9rem, 2vw, 1rem);
        font-weight: 500;
        color: var(--text-primary);
        margin: 0;
        line-height: 1.5;
        flex-shrink: 0;
        padding-top: clamp(0.375rem, 1vw, 0.5rem);
    }
    
    .rating-container {
        flex: 1;
        min-width: 0;
    }
}

// Align sistem - right
.star-rating-right {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);
    
    > label {
        width: clamp(140px, 20vw, 200px);
        font-size: clamp(0.9rem, 2vw, 1rem);
        font-weight: 500;
        color: var(--text-primary);
        text-align: right;
        margin: 0;
        line-height: 1.5;
        flex-shrink: 0;
        padding-top: clamp(0.375rem, 1vw, 0.5rem);
    }
    
    .rating-container {
        flex: 1;
        min-width: 0;
    }
}

// Top align (default)
.star-rating:not(.star-rating-left):not(.star-rating-right) {
    > label {
        display: block;
        font-size: clamp(0.9rem, 2vw, 1rem);
        font-weight: 500;
        color: var(--text-primary);
        margin: 0 0 clamp(0.625rem, 1.5vw, 0.75rem) 0;
        line-height: 1.5;
    }
}

// Rating container
.rating-container {
    background: var(--surface-color);
    backdrop-filter: blur(8px);
    border: 2px solid var(--border-color);
    border-radius: clamp(0.5rem, 1.5vw, 0.75rem);
    padding: clamp(1rem, 2.5vw, 1.25rem);
    transition: all 0.3s ease;
    
    &:hover {
        border-color: var(--primary);
        box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
    }
}

// Stars container
.stars {
    display: flex;
    gap: clamp(0.375rem, 1vw, 0.5rem);
    align-items: center;
    justify-content: flex-start;
    margin-bottom: clamp(0.625rem, 1.5vw, 0.75rem);
    
    .star {
        cursor: pointer;
        transition: all 0.3s ease;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: clamp(2rem, 4.5vw, 2.25rem);
        height: clamp(2rem, 4.5vw, 2.25rem);
        border-radius: 50%;
        
        svg {
            width: clamp(1.5rem, 3.5vw, 1.75rem);
            height: clamp(1.5rem, 3.5vw, 1.75rem);
            color: #d1d5db;
            transition: all 0.3s ease;
            filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
        }
        
        &:hover {
            background: rgba(255, 193, 7, 0.1);
            transform: scale(1.1);
            
            svg {
                transform: rotate(10deg);
            }
        }
        
        &.hovered svg,
        &.rated svg {
            color: #ffc107;
            transform: scale(1.05);
        }
        
        &.rated {
            &:hover {
                background: rgba(255, 193, 7, 0.15);
            }
            
            svg {
                filter: drop-shadow(0 3px 6px rgba(255, 193, 7, 0.3));
            }
        }
        
        // Yıldız parlaklık efekti
        &.rated::after {
            content: '';
            position: absolute;
            width: 4px;
            height: 4px;
            background: #fff;
            border-radius: 50%;
            top: 8px;
            right: 10px;
            opacity: 0.8;
            animation: sparkle 2s infinite;
        }
    }
}

// Rating text
.rating-text {
    display: flex;
    align-items: center;
    gap: clamp(0.375rem, 1vw, 0.5rem);
    font-size: clamp(0.8rem, 1.8vw, 0.875rem);
    color: var(--text-secondary);
    
    .rating-value {
        font-size: clamp(1rem, 2.2vw, 1.125rem);
        font-weight: 600;
        color: #ffc107;
        text-shadow: 0 1px 2px rgba(255, 193, 7, 0.3);
    }
    
    .rating-label {
        font-weight: 500;
    }
}

// Sparkle animasyonu
@keyframes sparkle {
    0%, 100% {
        opacity: 0.8;
        transform: scale(1);
    }
    50% {
        opacity: 1;
        transform: scale(1.2);
    }
}

// Mobil optimizasyonları
@media (max-width: 768px) {
    .star-rating-left,
    .star-rating-right {
        flex-direction: column;
        gap: 0;
        
        > label {
            width: 100%;
            text-align: left !important;
            padding-top: 0;
            margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
        }
        
        .rating-container {
            width: 100%;
        }
    }
    
    .rating-container {
        padding: clamp(0.875rem, 2vw, 1rem);
        border-radius: clamp(0.375rem, 1vw, 0.5rem);
    }
    
    .stars {
        gap: clamp(0.25rem, 0.8vw, 0.375rem);
        margin-bottom: clamp(0.5rem, 1.2vw, 0.625rem);
        
        .star {
            width: clamp(1.75rem, 4vw, 2rem);
            height: clamp(1.75rem, 4vw, 2rem);
            
            svg {
                width: clamp(1.25rem, 3vw, 1.5rem);
                height: clamp(1.25rem, 3vw, 1.5rem);
            }
            
            &:hover {
                transform: scale(1.05);
            }
        }
    }
    
    .rating-text {
        font-size: clamp(0.75rem, 1.6vw, 0.8rem);
        gap: clamp(0.25rem, 0.8vw, 0.375rem);
        
        .rating-value {
            font-size: clamp(0.875rem, 2vw, 1rem);
        }
    }
}

// Küçük ekranlar için
@media (max-width: 480px) {
    .star-rating-left,
    .star-rating-right {
        > label {
            font-size: 0.875rem;
            margin-bottom: 0.5rem;
        }
    }
    
    .star-rating:not(.star-rating-left):not(.star-rating-right) {
        > label {
            font-size: 0.875rem;
            margin-bottom: 0.625rem;
        }
    }
    
    .rating-container {
        padding: 0.75rem;
        border-radius: 0.375rem;
    }
    
    .stars {
        gap: 0.25rem;
        justify-content: center;
        margin-bottom: 0.5rem;
        
        .star {
            width: 1.75rem;
            height: 1.75rem;
            
            &:hover {
                transform: scale(1.03);
            }
            
            &:active {
                transform: scale(0.97);
            }
            
            svg {
                width: 1.25rem;
                height: 1.25rem;
            }
            
            // Mobilde sparkle efektini küçült
            &.rated::after {
                width: 3px;
                height: 3px;
                top: 6px;
                right: 8px;
            }
        }
    }
    
    .rating-text {
        justify-content: center;
        font-size: 0.75rem;
        gap: 0.25rem;
        
        .rating-value {
            font-size: 0.875rem;
        }
    }
}

// Dark mode devre dışı - Her zaman açık tema kullan
</style>